<template>
  <div class="tw-flex tw-gap-2">
    <CustomerActionButton
      v-for="action in visibleActions"
      :key="action.label"
      @click="action.onClick()"
      :tooltip="action.label"
    >
      <component
        :is="action.icon"
        v-if="action.icon"
        class="tw-w-5 tw-h-5"
        :class="action.iconClass"
      />
    </CustomerActionButton>

    <!-- Confirmation Modal -->
    <ConfirmationModal
      v-model="showConfirmationModal"
      :title="t('common.forms.confirmation').toUpperCase()"
      :message="confirmationMessage"
      :type="confirmationModalType"
      :confirm-text="t('common.forms.confirm')"
      :cancel-text="t('common.forms.cancel')"
      @confirm="onConfirmAction"
      @cancel="onCloseModal"
    />

    <!-- Request clarification modal -->
    <GenericModal
      v-model="showRequestClarificationModal"
      :title="t('payments.titles.confirm_clarification')"
    >
      <template #body>
        <div class="tw-mb-4">
          <Textarea
            v-model="clarificationComment"
            class="tw-border tw-w-full tw-px-3 tw-py-2 tw-rounded tw-resize-none"
            :placeholder="t('orders.titles.message_hint')"
            rows="6"
          ></Textarea>
        </div>
      </template>

      <template #footer>
        <div class="tw-flex tw-justify-end tw-gap-2 tw-items-center">
          <Button
            class="tw-bg-white tw-border-solid tw-border-2 tw-border-secondary-500 tw-text-secondary-500 hover:tw-bg-secondary-500 hover:tw-text-white tw-px-2 tw-py-2 tw-rounded-md tw-text-sm tw-flex tw-gap-2 tw-transition-colors"
            type="button"
            @click="onCloseRequestClarificationModal"
          >
            {{ t('common.forms.cancel') }}
          </Button>
          <Button
            class="tw-bg-secondary-500 tw-border-2 tw-border-secondary-500 tw-text-white hover:tw-bg-secondary-600 tw-border-solid tw-px-2 tw-py-2 tw-rounded-md tw-text-sm tw-flex tw-gap-2 tw-transition-colors"
            :class="{
              'tw-opacity-50 !tw-cursor-not-allowed': disableActions || !clarificationComment,
            }"
            type="button"
            @click="performClarificationAction"
            :disabled="disableActions || !clarificationComment"
          >
            {{ t('requests.actions.request_clarification') }}
          </Button>
        </div>
      </template>
    </GenericModal>
  </div>
</template>

<script setup lang="ts">
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';
import { useUserStore } from '@/stores/user.store';
import { storeToRefs } from 'pinia';
import { usePaymentStore } from '@/stores/payment.store';
import { useI18n } from 'vue-i18n';
import router from '@/router';
import CustomerActionButton from '@/components/shared/CustomerActionButton.vue';
import ConfirmationModal from '@/components/shared/ConfirmationModal.vue';
import GenericModal from '@/components/shared/GenericModal.vue';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useCustomerStore } from '@/stores/customer.store';
import { ref, shallowRef, computed } from 'vue';

import ApproveIcon from '@/assets/icons/Approve.svg';
import CloseIcon from '@/assets/icons/Close.svg';
import EditIcon from '@/assets/icons/Edit.svg';
import ForwardIcon from '@/assets/icons/Forward.svg';
import QuestionIcon from '@/assets/icons/Question.svg';
import DeleteIcon from '@/assets/icons/Trash.svg';
import PaidIcon from '@/assets/icons/PaidIcon.svg';
import UnpaidIcon from '@/assets/icons/UnpaidIcon.svg';
import CheckTextIcon from '@/assets/icons/CheckText.svg';

// Action type enum
enum ActionType {
  DELETE = 'delete',
  APPROVE = 'approve',
  REJECT = 'reject',
  CLARIFICATION = 'clarification',
  RESPOND_TO_CLARIFICATION = 'respond_to_clarification',
  SUBMIT = 'submit',
  TOGGLE_PAID = 'toggle_paid',
}

const { t } = useI18n();
const { handleApiError } = useApiUtils();
const { currentUser } = storeToRefs(useUserStore());

const customerStore = useCustomerStore();
const disableActions = computed(() => customerStore.shouldDisableCustomerActions());
const {
  paymentsBulkAction,
  createPaymentComments,
  deletePayment,
  togglePaidStatus,
  respondToClarificationRequest,
} = usePaymentStore();

const showConfirmationModal = ref(false);
const confirmationMessage = ref('');
const currentActionType = ref<ActionType | null>(null);
const confirmationModalType = ref<'warning' | 'danger' | 'info' | 'success'>('warning');

// Request clarification modal
const showRequestClarificationModal = ref(false);
const clarificationComment = ref('');

const props = defineProps<{
  payment: any;
}>();

const emit = defineEmits<{
  reloadTable: [];
}>();

// const performSubmitAction = async () => {
//   await handleApiError(async () => {
//     await paymentsBulkAction('bulk_submit', [props.payment.id]);
//     emit('reloadTable');
//   }, t('payments.titles.successful_submit'));
// };

const performApproveAction = async () => {
  await handleApiError(async () => {
    await paymentsBulkAction('bulk_approve', [props.payment.id]);
    emit('reloadTable');
  }, t('payments.titles.successful_approval'));
};

const performClarificationAction = async () => {
  await handleApiError(async () => {
    await paymentsBulkAction('bulk_request_clarification', [props.payment.id]);
    await createPaymentComments({
      payment: props.payment.id,
      text: clarificationComment.value,
    });
    emit('reloadTable');
    onCloseRequestClarificationModal();
    clarificationComment.value = '';
  }, t('payments.titles.successful_clarification'));
};

const onCloseRequestClarificationModal = () => {
  showRequestClarificationModal.value = false;
  clarificationComment.value = '';
};

const openRequestClarificationModal = () => {
  currentActionType.value = ActionType.CLARIFICATION;
  showRequestClarificationModal.value = true;
};

const performRespondToClarificationAction = async () => {
  await handleApiError(async () => {
    await respondToClarificationRequest(props.payment.id);
    emit('reloadTable');
  }, t('payments.titles.successful_clarification_completed'));
};

const performTogglePaidAction = async () => {
  const payed = props.payment.payed ? false : true;
  await handleApiError(
    async () => {
      await togglePaidStatus(props.payment.id, payed);
      emit('reloadTable');
    },
    payed ? t('payments.titles.successful_paid') : t('payments.titles.successful_unpaid')
  );
};

const performRejectAction = async () => {
  await handleApiError(async () => {
    await paymentsBulkAction('bulk_reject', [props.payment.id]);
    emit('reloadTable');
  }, t('payments.titles.successful_rejection'));
};

const editSinglePayment = () => {
  router.push({ name: 'edit-payment', params: { id: props.payment.id } });
};

const performDeleteAction = async () => {
  await handleApiError(async () => {
    await deletePayment(props.payment.id);
    emit('reloadTable');
  }, t('payments.titles.successful_delete'));
};

const actionMap = {
  // [ActionType.SUBMIT]: performSubmitAction,
  [ActionType.APPROVE]: performApproveAction,
  [ActionType.REJECT]: performRejectAction,
  [ActionType.DELETE]: performDeleteAction,
  [ActionType.CLARIFICATION]: performClarificationAction,
  [ActionType.TOGGLE_PAID]: performTogglePaidAction,
  [ActionType.RESPOND_TO_CLARIFICATION]: performRespondToClarificationAction,
};

const actionTypeToMessageMap = {
  // [ActionType.SUBMIT]: t('payments.titles.confirm_submit'),
  [ActionType.APPROVE]: t('payments.titles.confirm_approval'),
  [ActionType.REJECT]: t('payments.titles.confirm_rejection'),
  [ActionType.DELETE]: t('payments.titles.confirm_delete'),
  [ActionType.CLARIFICATION]: t('payments.titles.confirm_clarification'),
  [ActionType.TOGGLE_PAID]: t('payments.titles.confirm_toggle_paid'),
};

const actionTypeToModalTypeMap = {
  // [ActionType.SUBMIT]: 'info',
  [ActionType.APPROVE]: 'success',
  [ActionType.REJECT]: 'warning',
  [ActionType.DELETE]: 'danger',
  [ActionType.CLARIFICATION]: 'info',
  [ActionType.TOGGLE_PAID]: 'info',
} as const;

const openConfirmationModal = (actionType: ActionType) => {
  confirmationModalType.value = actionTypeToModalTypeMap[actionType] || 'warning';
  currentActionType.value = actionType;
  confirmationMessage.value = actionTypeToMessageMap[actionType];
  showConfirmationModal.value = true;
};

const onConfirmAction = async () => {
  if (currentActionType.value === null) return;
  await actionMap[currentActionType.value]();
  showConfirmationModal.value = false;
};

const onCloseModal = () => {
  showConfirmationModal.value = false;
};

const showEditPaymentAction = () => {
  return (
    (props.payment.status === 'DRAFT' && props.payment.created_by === currentUser.value?.id) ||
    (props.payment.is_clarification_requested && props.payment.created_by === currentUser.value?.id)
  );
};

// const showSubmitAction = () => {
//   return props.payment.status === 'DRAFT';
// };

const showApproveOrRejectAction = () => {
  if (currentUser.value?.user_role?.includes('Admin')) {
    return ['PENDING_APPROVAL', 'CLEARING', 'CLARIFICATION_REQUESTED'].includes(
      props.payment?.status_display
    );
  }
  if (currentUser.value?.user_role?.includes('Approver')) {
    return props.payment?.status_display === 'PENDING_APPROVAL';
  }
  if (currentUser.value?.user_role?.includes('Payment Clearer')) {
    return ['PENDING_APPROVAL', 'CLARIFICATION_REQUESTED'].includes(props.payment?.status_display);
  }
  return false;
};

const showRequestClarificationAction = () => {
  if (
    currentUser.value?.user_role?.includes('Admin') ||
    currentUser.value?.user_role?.includes('Approver') ||
    currentUser.value?.user_role?.includes('Payment Clearer')
  ) {
    return (
      ['PENDING_APPROVAL', 'CLEARING'].includes(props.payment?.status_display) &&
      !props.payment?.is_clarification_requested
    );
  }
  return false;
};

const showRespondToClarificationAction = () => {
  if (['APPROVED', 'REJECTED'].includes(props.payment.status)) {
    return false;
  }
  return (
    props.payment?.is_clarification_requested &&
    (currentUser.value?.id === props.payment.created_by ||
      currentUser.value?.id === props.payment.requested_clarification_by)
  );
};

const showTogglePaidAction = () => {
  return (
    (currentUser.value?.user_role?.includes('Payer') ||
      currentUser.value?.user_role?.includes('Payment Clearer')) &&
    props.payment.status_display === 'APPROVED'
  );
};

const actions = shallowRef<any[]>([
  // Submit action disabled — submission must go through the details page
  // to ensure the AdjustPaymentForSubmission modal is shown
  // {
  //   label: t('common.forms.submit'),
  //   icon: ForwardIcon,
  //   iconClass: 'tw-text-blue-500',
  //   onClick: () => {
  //     openConfirmationModal(ActionType.SUBMIT);
  //   },
  //   show: () => {
  //     return showSubmitAction();
  //   },
  //   actionType: ActionType.SUBMIT,
  // },
  {
    label: t('requests.actions.approve'),
    icon: ApproveIcon,
    iconClass: 'tw-text-green-500',
    onClick: () => {
      openConfirmationModal(ActionType.APPROVE);
    },
    show: () => {
      return showApproveOrRejectAction();
    },
    actionType: ActionType.APPROVE,
  },
  {
    label: t('requests.actions.request_clarification'),
    icon: QuestionIcon,
    iconClass: 'tw-text-yellow-500',
    onClick: () => {
      openRequestClarificationModal();
    },
    show: () => {
      return showRequestClarificationAction();
    },
    actionType: ActionType.CLARIFICATION,
  },
  {
    label: t('orders.actions.mark_clarifications_completed'),
    icon: CheckTextIcon,
    iconClass: 'tw-text-green-500',
    onClick: () => {
      openConfirmationModal(ActionType.RESPOND_TO_CLARIFICATION);
    },
    show: () => {
      return showRespondToClarificationAction();
    },
    actionType: ActionType.RESPOND_TO_CLARIFICATION,
  },
  {
    label: t('requests.actions.reject'),
    icon: CloseIcon,
    iconClass: 'tw-text-red-500',
    onClick: () => {
      openConfirmationModal(ActionType.REJECT);
    },
    show: () => {
      return showApproveOrRejectAction();
    },
    actionType: ActionType.REJECT,
  },
  {
    label: t('common.forms.edit'),
    icon: EditIcon,
    iconClass: 'tw-text-primary-600',
    onClick: () => {
      editSinglePayment();
    },
    show: () => {
      return showEditPaymentAction();
    },
    actionType: null, // Edit doesn't need confirmation
  },
  {
    label: t('common.forms.delete'),
    icon: DeleteIcon,
    iconClass: 'tw-text-red-500',
    onClick: () => {
      openConfirmationModal(ActionType.DELETE);
    },
    show: () => {
      return (
        props.payment.status === 'DRAFT' && props.payment.released_by === currentUser.value?.id
      );
    },
    actionType: ActionType.DELETE,
  },
  {
    label: props.payment.payed ? t('common.forms.mark_as_unpaid') : t('common.forms.mark_as_paid'),
    icon: props.payment.payed ? PaidIcon : UnpaidIcon,
    iconClass: props.payment.payed ? 'tw-text-red-500' : 'tw-text-green-500',
    onClick: () => {
      openConfirmationModal(ActionType.TOGGLE_PAID);
    },
    show: () => {
      return showTogglePaidAction();
    },
    actionType: ActionType.TOGGLE_PAID,
  },
]);

const visibleActions = computed(() => {
  return actions.value.filter(action => action.show());
});
</script>
